import { CurrencyCode } from 'further-types/lp/vehicle';
import { z } from 'zod';
import { AssetType } from 'further-types/lp/asset';
import { CoreHoldingClassSchema } from '../holding-class';
import {
  isMongoId,
  optionalCountryCodeValidator,
  withPaginationParams,
} from '../../helpers';

const CoreCompanySchema = z.object({
  assetType: z.literal(AssetType.Company),
  vehicleId: z.string().refine(isMongoId(), {
    message: 'Invalid vehicle ID',
  }),
  tradingName: z.string().min(1, { message: 'Trading name is required.' }),
  fullLegalName: z.string().min(1, { message: 'Full legal name is required.' }),
  baseCurrency: z.nativeEnum(CurrencyCode, {
    required_error: 'Base currency is required',
  }),
  registeredNumber: z.string().optional(),
  registrationCountry: z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      if (!optionalCountryCodeValidator(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please provide a valid country code',
        });
      }
    }),
  registeredAddressLine1: z.string().optional(),
  registeredAddressLine2: z.string().optional(),
  registeredAddressTownCity: z.string().optional(),
  registeredAddressPostalCode: z.string().optional(),
  registeredAddressCountry: z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      if (!optionalCountryCodeValidator(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please provide a valid country code',
        });
      }
    }),
  holdingClasses: z.array(CoreHoldingClassSchema).optional(),
  newHoldingClasses: z.array(CoreHoldingClassSchema).optional(),
});

const PortalInformationSchema = z.object({
  description: z.string().optional(),
  companyLogo: z
    .object({
      path: z.string(),
      name: z.string(),
      preview: z.string(),
    })
    .optional()
    .nullable(),
  websiteUrl: z.string().optional(),
  twitterUrl: z.string().optional(),
  linkedinUrl: z.string().optional(),
  // TODO: add stage and sector
  stage: z.string().optional(),
  sector: z.string().optional(),
});

export const CompanySchema = CoreCompanySchema.extend(
  PortalInformationSchema.shape,
);

export type CompanySchemaFormValues = z.infer<typeof CompanySchema>;

export type PortalInformationSchemaFormValues = z.infer<
  typeof PortalInformationSchema
>;

export const GetCompanyById = z.object({
  companyId: z.string().refine(isMongoId(), {
    message: 'Invalid company ID',
  }),
  vehicleId: z.string().refine(isMongoId(), {
    message: 'Invalid vehicle ID',
  }),
});

export const UpdateCompany = CompanySchema.extend({
  companyId: z.string().refine(isMongoId(), {
    message: 'Invalid company ID',
  }),
});

export const ListCompanies = withPaginationParams(
  z.object({
    searchQuery: z.string().optional(),
  }),
  ['name'],
);

export type ListCompaniesInput = z.infer<typeof ListCompanies>;

export type GetCompanyByIdInput = z.infer<typeof GetCompanyById>;
